import { MerchantValidationError } from "../error";
import * as Fields from "../fields";
import { FieldSet } from "../fieldset";
import { Merchant } from "../merchant";
import {
  Acquirer,
  CountryList,
  ECommercePlatform,
  Industry,
  SalesChannel,
  SubIndustry,
  TotalAnnualSales,
} from "../types";

export type LeadFieldSetValueMap = {
  acquirer: Acquirer;
  contactlessPayment: string;
  email: string;
  firstName: string;
  franchisee: string;
  franchiseName: string;
  industry: Industry;
  lastName: string;
  numOfStores: number;
  phone: string;
  platform: ECommercePlatform;
  registeredCountry: CountryList;
  salesChannel: SalesChannel;
  storeName: string;
  subIndustry: SubIndustry;
  totalAnnualSales: TotalAnnualSales;
  totalAnnualSalesInStore: number;
  website: string;
};

export type LeadFieldSetValueMapper<T> = {
  [K in keyof LeadFieldSetValueMap]: T;
};

export class LeadFieldSet extends FieldSet<LeadFieldSetValueMap> {
  static fromJson(
    Lead: LeadFieldSet,
    {
      acquirer,
      contactlessPayment,
      firstName,
      franchisee,
      franchiseName,
      industry,
      lastName,
      numOfStores,
      phone,
      platform,
      registeredCountry,
      salesChannel,
      storeName,
      subIndustry,
      totalAnnualSales,
      totalAnnualSalesInStore,
      website,
    }: Partial<LeadFieldSetValueMap>
  ): LeadFieldSet {
    Lead.acquirer.value = acquirer;
    Lead.contactlessPayment.value = contactlessPayment;
    Lead.firstName.value = firstName;
    Lead.franchisee.value = franchisee;
    Lead.franchiseName.value = franchiseName;
    Lead.industry.value = industry;
    Lead.lastName.value = lastName;
    Lead.numOfStores.value = numOfStores;
    Lead.phone.value = phone;
    Lead.platform.value = platform;
    Lead.registeredCountry.value = registeredCountry;
    Lead.salesChannel.value = salesChannel;
    Lead.storeName.value = storeName;
    Lead.subIndustry.value = subIndustry;
    Lead.totalAnnualSales.value = totalAnnualSales;
    Lead.totalAnnualSalesInStore.value = totalAnnualSalesInStore;
    Lead.website.value = website;

    return Lead;
  }

  public acquirer: Fields.AcquirerField;
  public contactlessPayment: Fields.ContactlessPaymentField;
  public email: Fields.EmailField;
  public firstName: Fields.FirstNameField;
  public franchisee: Fields.FranchiseeField;
  public franchiseName: Fields.FranchiseNameField;
  public industry: Fields.IndustryField;
  public lastName: Fields.LastNameField;
  public numOfStores: Fields.NumberOfStoresField;
  public phone: Fields.PhoneField;
  public platform: Fields.PlatformField;
  public registeredCountry: Fields.RegisteredCountryField;
  public salesChannel: Fields.SalesChannelField;
  public storeName: Fields.StoreNameField;
  public subIndustry: Fields.SubIndustryField;
  public totalAnnualSales: Fields.TotalAnnualSalesField;
  public totalAnnualSalesInStore: Fields.TotalAnnualSalesInStoreField;
  public website: Fields.WebsiteField;

  constructor(model: Merchant) {
    super();

    this.fieldSetName = "LeadFieldSet";

    this.acquirer = new Fields.AcquirerField(model, this);
    this.contactlessPayment = new Fields.ContactlessPaymentField(model, this);
    this.email = new Fields.EmailField(model, {
      propertyName: "email",
    });
    this.firstName = new Fields.FirstNameField(model, null);
    this.franchisee = new Fields.FranchiseeField(model);
    this.franchiseName = new Fields.FranchiseNameField(model);
    this.industry = new Fields.IndustryField(model, this);
    this.lastName = new Fields.LastNameField(model, null);
    this.numOfStores = new Fields.NumberOfStoresField(model, this);
    this.phone = new Fields.PhoneField(model);
    this.platform = new Fields.PlatformField(model, this);
    this.registeredCountry = new Fields.RegisteredCountryField(model);
    this.salesChannel = new Fields.SalesChannelField(model, this);
    this.storeName = new Fields.StoreNameField(model, this);
    this.subIndustry = new Fields.SubIndustryField(model, this);
    this.totalAnnualSales = new Fields.TotalAnnualSalesField(model, this);
    this.totalAnnualSalesInStore = new Fields.TotalAnnualSalesInStoreField(
      model,
      this
    );
    this.website = new Fields.WebsiteField(model, this);

    this.fields = [
      this.acquirer,
      this.contactlessPayment,
      this.email,
      this.firstName,
      this.franchisee,
      this.franchiseName,
      this.industry,
      this.lastName,
      this.numOfStores,
      this.phone,
      this.platform,
      this.registeredCountry,
      this.salesChannel,
      this.storeName,
      this.subIndustry,
      this.totalAnnualSales,
      this.totalAnnualSalesInStore,
      this.website,
    ];
  }

  validate({
    stringify = true,
    fieldPropertyListToValidate,
  }: {
    stringify: boolean;
    fieldPropertyListToValidate?: string[];
  }): void {
    const Errors = new MerchantValidationError();

    let errorCount = 0;
    const fieldsToValidate = fieldPropertyListToValidate
      ? this.fields.filter((f) =>
          fieldPropertyListToValidate.includes(f.propertyName)
        )
      : this.fields;
    for (const field of fieldsToValidate) {
      try {
        field.validate();
      } catch (ex) {
        errorCount++;
        if (Errors.fieldErrorMap.lead) {
          Errors.fieldErrorMap.lead[
            field.propertyName as keyof LeadFieldSetValueMap
          ] = {
            error: stringify ? ex.message : ex,
          };
        }
      }
    }

    if (errorCount) {
      throw Errors.fieldErrorMap;
    }
  }
}
